import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TransactionTypeSelect } from '@client/components/_shared/selectors/financial/TransactionTypeSelect';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { buildCurrencyInputRules } from '@client/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Input } from 'antd';
import type { Decimal } from 'decimal.js';
import { useEffect, useId } from 'react';
import { claimFormStore } from '../../../../store';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/reserves/details/$reserveId/reserve-adjustments/add',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Add adjustment',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReserveAdjustmentEdit />
        </PageController>
    );
}

type Adjustment = {
    transactionTypeId: bigint;
    description: string;
    amount: Decimal;
};

const FormItem = Form.Item<Adjustment>;

function ReserveAdjustmentEdit() {
    const formId = useId();
    const isReadonly = claimFormStore.use.isReadonly();
    const claim = claimFormStore.use.formData();
    const { reserveId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.reserveAdjustment.createReserveAdjustment.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<Adjustment>();

    useEffect(() => {
        if (isReadonly) {
            navigate({ moveInMatches: -1 });
        }
    }, [isReadonly]);

    if (isReadonly) {
        return null;
    }

    return (
        <OverlayPage
            title="Add reserve adjustment"
            afterClose={() => {
                form.resetFields();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" htmlType="submit" form={formId}>
                        Save
                    </Button>
                </>
            )}
        >
            {({ onClose }) => (
                <Form
                    form={form}
                    id={formId}
                    layout="vertical"
                    onFinish={(values) => {
                        message.loading('Saving...');

                        mutate(
                            {
                                description: values.description,
                                claimId: claim.id,
                                transactionTypeId: values.transactionTypeId,
                                reserveId,
                                amount: values.amount,
                            },
                            {
                                onSuccess() {
                                    message.success('Saved.');
                                    void trpcUtils.reserve.invalidate();
                                    void trpcUtils.reserveAdjustment.invalidate();
                                    void trpcUtils.claim.getFinancialSummary.invalidate();
                                    onClose();
                                },
                                onError(error) {
                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                >
                    {claim.productTypeId && (
                        <FormItem label="Transaction type" name="transactionTypeId" rules={[{ required: true }]}>
                            <TransactionTypeSelect
                                transactionNature={['RESERVE_INCREASE', 'RESERVE_DECREASE']}
                                productTypeId={claim.productTypeId}
                            />
                        </FormItem>
                    )}
                    <FormItem
                        label="Adjustment amount"
                        name="amount"
                        rules={buildCurrencyInputRules({ isRequired: true, gtZero: true })}
                    >
                        <CurrencyInput />
                    </FormItem>
                    <FormItem label="Description" name="description" rules={[{ required: true }]}>
                        <Input.TextArea />
                    </FormItem>
                </Form>
            )}
        </OverlayPage>
    );
}
